import { useCallback } from 'react';
import { useAtomValue } from 'jotai';
import {
  $ABTest,
  $FallbackGpToApk,
  $RootTraffic,
  $SearchParams,
  $UserAgent,
  $WebUser,
} from '@lemon8/web-app-shared/atom';
import { useSearchParams } from '@remix-run/react';
import { $EnablePinterest } from 'shared/atoms';
import { EClickPosition } from '~/utils/click-position';

const appendTrafficInfoToOneLink = (onelink: URL, hrefParams: URLSearchParams) => {
  const paramsList = [
    'pid',
    'af_siteid',
    'c',
    'af_adset',
    'af_adset_id',
    'af_ad',
    'af_ad_id',
    'af_c_id',
    'af_click_lookback',
    'clickid',
    'sha1_idfa',
    'tracking',
    'appid',
    'timestamp',
    'af_prt',
  ];

  paramsList.forEach((param) => {
    const value = hrefParams.get(param);

    if (value) {
      onelink.searchParams.set(param, value);
    }
  });
};

export const useAndroidDownload = () => {
  const { trafficPid } = useAtomValue($RootTraffic);
  const enablePintrk = useAtomValue($EnablePinterest);
  const { versions } = useAtomValue($ABTest);
  const [search] = useSearchParams();
  const { webId } = useAtomValue($WebUser);

  return useCallback(() => {
    const uri = new URL('https://lemon8.onelink.me/FMQw');
    uri.searchParams.set('pid', 'website_homepage');
    uri.searchParams.set('af_dp', 'snssdk2657://main?channel_id=0&tab=home&pid=website_homepage');
    uri.searchParams.set('af_web_dp', 'https://play.google.com/store/apps/details?id=com.bd.nproject');
    uri.searchParams.set(
      'amp_extra',
      JSON.stringify({
        enter_position: 'download_googleplay',
        enter_page_type: 'website_homepage',
        enter_page_id: 'website_homepage',
        traffic_type: trafficPid,
        web_id: webId,
      }),
    );
    uri.searchParams.set('ab_version', versions);

    window.Tea('wap_click', {
      current_position: 'download_googleplay',
    });

    if (enablePintrk && 'pintrk' in window) {
      (window as any).pintrk('track', 'lead', {
        event_id: 'eventId0001',
        lead_type: 'Newsletter',
      });
    }

    appendTrafficInfoToOneLink(uri, search);

    window.open(uri.toString(), '_blank');
  }, [trafficPid, enablePintrk, search]);
};

export const useIOSDownload = () => {
  const { trafficPid } = useAtomValue($RootTraffic);
  const enablePintrk = useAtomValue($EnablePinterest);
  const { versions } = useAtomValue($ABTest);
  const [search] = useSearchParams();
  const { webId } = useAtomValue($WebUser);

  return useCallback(() => {
    const uri = new URL('https://lemon8.onelink.me/FMQw');
    uri.searchParams.set('pid', 'website_homepage');
    uri.searchParams.set('af_dp', 'snssdk2657://main?channel_id=0&tab=home&pid=website_homepage');
    uri.searchParams.set('af_web_dp', 'https://apps.apple.com/app/apple-store/id1498607143');
    uri.searchParams.set(
      'amp_extra',
      JSON.stringify({
        enter_position: 'download_appstore',
        enter_page_type: 'website_homepage',
        enter_page_id: 'website_homepage',
        traffic_type: trafficPid,
        web_id: webId,
      }),
    );
    uri.searchParams.set('ab_version', versions);

    window.Tea('wap_click', {
      current_position: 'download_appstore',
    });

    if (enablePintrk && 'pintrk' in window) {
      (window as any).pintrk('track', 'lead', {
        event_id: 'eventId0001',
        lead_type: 'Newsletter',
      });
    }

    appendTrafficInfoToOneLink(uri, search);
    window.open(uri.toString(), '_blank');
  }, [trafficPid, enablePintrk, search]);
};

export const useMobileDownload = () => {
  const { trafficPid } = useAtomValue($RootTraffic);
  const { isiOS } = useAtomValue($UserAgent);
  const enablePintrk = useAtomValue($EnablePinterest);
  const [search] = useSearchParams();
  const { versions } = useAtomValue($ABTest);
  const { webId } = useAtomValue($WebUser);

  return useCallback(() => {
    const uri = new URL('https://lemon8.onelink.me/FMQw');
    uri.searchParams.set('pid', 'website_homepage');
    uri.searchParams.set('af_dp', 'snssdk2657://main?channel_id=0&tab=home&pid=website_homepage');
    uri.searchParams.set(
      'af_web_dp',
      isiOS
        ? 'https://itunes.apple.com/app/apple-store/id1498607143'
        : 'https://play.google.com/store/apps/details?id=com.bd.nproject',
    );
    uri.searchParams.set(
      'amp_extra',
      JSON.stringify({
        enter_position: isiOS ? 'download_appstore' : 'download_googleplay',
        enter_page_type: 'website_homepage',
        traffic_type: trafficPid,
        web_id: webId,
      }),
    );
    uri.searchParams.set('ab_version', versions);

    window.Tea('wap_click', {
      current_position: isiOS ? 'download_appstore' : 'download_googleplay',
    });

    if (enablePintrk && 'pintrk' in window) {
      window.pintrk('track', 'lead', {
        event_id: 'eventId0001',
        lead_type: 'Newsletter',
      });
    }

    appendTrafficInfoToOneLink(uri, search);
    window.open(uri.toString(), '_blank');
  }, [isiOS, trafficPid, enablePintrk, search]);
};

interface IActiveProps {
  currentPosition: EClickPosition;
}

export const activeToFeed = (props: IActiveProps): void => {
  const { currentPosition } = props;

  const store = window.store;
  const { trafficPid } = store.get($RootTraffic);
  const { isiOS } = store.get($UserAgent);
  const enablePintrk = store.get($EnablePinterest);
  const search = store.get($SearchParams);
  const { versions } = store.get($ABTest);
  const { webId } = store.get($WebUser);
  const fallbackGpToApkConfig = store.get($FallbackGpToApk);

  const uri = new URL('https://lemon8.onelink.me/FMQw');
  uri.searchParams.set('pid', 'website_homepage');
  uri.searchParams.set('af_dp', 'snssdk2657://main?channel_id=0&tab=home&pid=website_homepage');

  uri.searchParams.set(
    'amp_extra',
    JSON.stringify({
      enter_position: currentPosition,
      enter_page_type: 'website_homepage',
      traffic_type: trafficPid,
      web_id: webId,
    }),
  );
  uri.searchParams.set('ab_version', versions);

  if (enablePintrk && 'pintrk' in window) {
    window.pintrk('track', 'lead', {
      event_id: 'eventId0001',
      lead_type: 'Newsletter',
    });
  }

  if ([EClickPosition.DOWNLOAD_APPSTORE, EClickPosition.DOWNLOAD_GOOGLE_PLAY].includes(currentPosition)) {
    uri.searchParams.set(
      'af_web_dp',
      currentPosition === EClickPosition.DOWNLOAD_APPSTORE
        ? 'https://itunes.apple.com/app/apple-store/id1498607143?pt=1613620&ct=interstitialdownload&mt=8'
        : 'https://play.google.com/store/apps/details?id=com.bd.nproject',
    );
  } else {
    uri.searchParams.set(
      'af_web_dp',
      isiOS
        ? 'https://itunes.apple.com/app/apple-store/id1498607143'
        : 'https://play.google.com/store/apps/details?id=com.bd.nproject',
    );
  }

  appendTrafficInfoToOneLink(uri, search);

  if (fallbackGpToApkConfig.enable) {
    uri.searchParams.set('af_web_dp', fallbackGpToApkConfig.apkLink);
    uri.searchParams.set('af_android_url', fallbackGpToApkConfig.apkLink);
  }

  window.open(uri.toString());
};
